import React, { ReactElement } from "react";

import AddIcon from '@mui/icons-material/Add';

import ChipList from "../../chipList";
import MultiSelectCalloutWithSearch from "./multiSelectCalloutWithSearch";
import { ChipListStyling } from "../../models";

import { IMultiSelectItem } from "modules/common/models";
import "./../styles/calloutMultiselect.sass";

interface IMultiselectAndChipsWithSearch<T extends IMultiSelectItem<T>> {
    defaultOptions: IMultiSelectItem<T>[];
    selectedOptions?: T[];
    containerClassName?: string;
    buttonClassName?: string;
    buttonText?: string;
    truncateAtIndex?: number;
    onChange: (items: T[]) => void;
    onClose?: () => void;
    onChipRemove?: (items: T[]) => void;
    nestingAllowed?: boolean;
    footerComponent?: ReactElement;
    calloutContainerStyle?: React.CSSProperties;
    searchPlaceholder?: string;
    chipListStyle?: ChipListStyling;
    buttonCssStyle?: React.CSSProperties;
    disableOptions?: boolean;
    calloutFooterStyle?: React.CSSProperties;
    buttonId: string;
}

/**
 * Button and list component
 * - button opens multi-select
 * - selections displayed in chips list
 */
const MultiselectAndChipsWithSearch = <TItem extends IMultiSelectItem<TItem>> (props: IMultiselectAndChipsWithSearch<TItem>) => {
    const {
        defaultOptions,
        containerClassName,
        selectedOptions = [],
        buttonClassName,
        buttonText,
        onChange,
        onClose,
        onChipRemove,
        truncateAtIndex = 4,
        nestingAllowed = true,
        footerComponent,
        calloutContainerStyle,
        searchPlaceholder,
        chipListStyle,
        buttonCssStyle,
        disableOptions,
        calloutFooterStyle,
        buttonId
    } = props;

    const onUpdateItems = (item: TItem) => {
        const isAlreadySelected: boolean = selectedOptions.map(opt => opt.id).includes(item.id);

        const updatedItemList = isAlreadySelected
        ? selectedOptions.filter(i => i.id !== item.id)
        : [...selectedOptions, item]

        if (onChipRemove) {
            onChipRemove(updatedItemList)
        }
        else {
            onChange(updatedItemList);
        }
    };

    return (
        <div className={`${containerClassName || ""}`}>
            <div style={{ width: "100%" }}>
                {defaultOptions.length !== 0 &&
                    <MultiSelectCalloutWithSearch
                        defaultOptions={defaultOptions}
                        containerClassName={containerClassName}
                        selectedOptions={selectedOptions}
                        onChange={onChange}
                        buttonStyle={{
                            startIcon: <AddIcon />,
                            ...(buttonText && {buttonText}),
                            ...(buttonClassName && {buttonClassName}),
                            ...(buttonCssStyle && {buttonCssStyle})
                        }}
                        nestingAllowed={nestingAllowed}
                        footerComponent={footerComponent}
                        calloutContainerStyle={calloutContainerStyle}
                        searchBarText={searchPlaceholder}
                        disableOptions={disableOptions}
                        calloutFooterStyle={calloutFooterStyle}
                        buttonId={buttonId}
                        onClose={onClose}
                    />
                }
            </div>
            <div className={`chip-list-container ${chipListStyle?.chipListClassName}`}>
                <ChipList
                    closable
                    onClose={onUpdateItems}
                    items={selectedOptions}
                    truncateAtIndex={truncateAtIndex}
                    emptyChipDetails={chipListStyle?.emptyChipStyling}
                />
            </div>
        </div>
    );
};

export default MultiselectAndChipsWithSearch;
